"use client";

import { formatWorkspaceDate } from "@/lib/display-preferences";

import { useEffect, useMemo, useState } from "react";
import type { ConnectorSnapshot } from "@/lib/connectors/types";
import { simulationModels, type SimulationRun, type SimulationModelId } from "@/lib/simulations";
import { systems } from "@/lib/systems";
import EngineSimulation from "./engine-simulation";
import QuantitativeSimulations from "./quantitative-simulations";
import QuantitativeStudies from "./quantitative-studies";

type SimulationsWorkspaceProps = {
  connectors: ConnectorSnapshot[];
  runs: SimulationRun[];
  loading: boolean;
  onRun: () => void;
  focusId?: string | null;
  focusCycle?: string | null;
  onOpenBelief?: (id: string, cycle: string) => void;
};

function probabilityLabel(basisPoints: number) {
  return `${(basisPoints / 100).toFixed(2)}%`;
}

function runTimeLabel(value: string | null) {
  if (!value) return "Unanchored";
  return formatWorkspaceDate(new Date(value), {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function metricLabel(value: number, unit: "%" | "USD", label = "") {
  if (unit === "USD") {
    return `$${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  }
  return `${value >= 0 && !/probability/i.test(label) ? "+" : ""}${value.toFixed(2)}%`;
}

export default function SimulationsWorkspace({
  connectors,
  runs,
  loading,
  onRun,
  focusId,
  focusCycle,
  onOpenBelief,
}: SimulationsWorkspaceProps) {
  const [illustrative, setIllustrative] = useState(Boolean(simulationModels.find(model => model.id === focusId)));
  const [studyId, setStudyId] = useState<string | null>(() => {
    const selected = focusId ? (focusId.startsWith("quant-study:") ? focusId.slice("quant-study:".length) : null)
      : typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("study") : null;
    return selected && /^[a-z][a-z0-9_-]{0,100}$/.test(selected) ? selected : null;
  });
  useEffect(() => {
    const url = new URL(window.location.href);
    if (studyId) url.searchParams.set("study", studyId); else url.searchParams.delete("study");
    if (url.href !== window.location.href) window.history.replaceState(window.history.state, "", url);
  }, [studyId]);
  const [selectedModel, setSelectedModel] = useState<SimulationModelId | null>(() => simulationModels.find(model => model.id === focusId)?.id ?? null);
  const [engineSelected, setEngineSelected] = useState(() => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("engine") === "simulation");
  useEffect(() => { window.scrollTo({top:0}); }, [selectedModel]);
  const connectorById = useMemo(
    () => new Map(connectors.map((connector) => [connector.id, connector])),
    [connectors],
  );
  const runByModel = useMemo(
    () => new Map(runs.map((run) => [run.modelId, run])),
    [runs],
  );


  if (engineSelected) return <EngineSimulation onBack={() => setEngineSelected(false)}/>;
  if (!illustrative) return <section className="workspace simulations" id="workspace" aria-labelledby="simulations-title">
    <header className="page-heading"><div><h1 id="simulations-title">Simulation</h1><p className="lede">Test what you believe.</p></div></header>
    {!studyId && <QuantitativeSimulations focusId={focusId?.startsWith("quant-study:") ? null : focusId} focusCycle={focusCycle} onOpenBelief={onOpenBelief}/>}
    <QuantitativeStudies key={studyId ?? "studies"} selectedId={studyId} onSelect={setStudyId} onOpenBelief={onOpenBelief}/>
    <div className="simulation-other-models"><button type="button" onClick={() => setEngineSelected(true)}>BTC payoff simulation →</button><button type="button" onClick={() => setIllustrative(true)}>Illustrative scenarios →</button></div>
  </section>;
  return (
    <section className={`workspace simulations ${selectedModel ? "simulation-selected" : ""}`} id="workspace" aria-labelledby="simulations-title">
      <header className="page-heading">
        <div>
          {!selectedModel && <button type="button" className="back-link" onClick={() => setIllustrative(false)}>← Simulation</button>}
          {selectedModel && <button type="button" className="back-link" onClick={() => setSelectedModel(null)}>← Simulation</button>}
          <h1 id="simulations-title">{selectedModel ? simulationModels.find(model => model.id === selectedModel)?.name.replace(" ensemble", "") : "Illustrative scenarios"}</h1>
          <p className="lede">Explore possible outcomes.</p>
        </div>
        <div className="connection-summary" aria-live="polite">
          <strong>{simulationModels.length} illustrative models</strong>

        </div>
      </header>

      {!selectedModel && <button className="engine-simulation-entry" type="button" onClick={() => setEngineSelected(true)}><strong>BTC market outlook</strong><span>World Model inputs · Kalshi payoffs · Saved decisions</span></button>}
      <div className="simulation-controls">
        <span>Latest ensemble {runTimeLabel(runs[0]?.asOf ?? null)}</span>
        <button type="button" onClick={onRun} disabled={loading}>
          {loading ? "Waiting for inputs" : "Run ensemble"}
        </button>
      </div>

      <div className="simulation-register" aria-busy={loading}>
        {!selectedModel && <div className="simulation-list-heading"><span>Model</span><span>Horizon</span><span>Status</span><span /></div>}
        {simulationModels.filter(model => !selectedModel || model.id === selectedModel).map((model, modelIndex) => {
          const run = runByModel.get(model.id);
          const featuredMetrics = run?.histogram ? run.metrics.filter(metric => /mean return|loss probability|expected shortfall/i.test(metric.label)) : run?.metrics ?? [];
          const otherMetrics = run?.metrics.filter(metric => !featuredMetrics.includes(metric)) ?? [];
          const linkedSystems = model.systemIds
            .map((id) => systems.find((system) => system.id === id)?.name ?? id);

          return (
            <details
              className="simulation-model"
              id={`simulation-${model.id}`}
              key={model.id}
              open={selectedModel === model.id}
            >
              <summary onClick={event => { event.preventDefault(); setSelectedModel(model.id); }}>
                <span className="simulation-index">{String(modelIndex + 1).padStart(2, "0")}</span>
                <div className="simulation-name">
                  <small>{model.purpose}</small>
                  <strong>{model.name.replace(" ensemble", "")}</strong>
                </div>
                <span className="simulation-horizon">{model.horizonDays} days</span>
                <span className="simulation-particles">{model.particleCount.toLocaleString()} paths</span>
                <div className="simulation-confidence">
                  <span>Uncalibrated</span><span aria-hidden="true">→</span>
                </div>
              </summary>

              <div className="simulation-detail">
                {run?.histogram && <details className="simulation-distribution-detail"><summary>Full return distribution</summary><figure className="simulation-distribution"><figcaption>Return distribution · {run.particleCount.toLocaleString()} simulated paths</figcaption><div className="distribution-bars" role="img" aria-label={`Distribution of ${run.particleCount} simulated returns from ${run.histogram[0].lower.toFixed(1)} to ${run.histogram.at(-1)!.upper.toFixed(1)} percent`}>{run.histogram.map((bin, index) => <span key={index} title={`${bin.lower.toFixed(1)}% to ${bin.upper.toFixed(1)}%: ${bin.count} paths`} style={{ height: `${Math.max(1, bin.count / Math.max(...run.histogram!.map(value => value.count)) * 100)}%` }} />)}</div><div className="distribution-axis"><span>{run.histogram[0].lower.toFixed(0)}%</span><span>Terminal return</span><span>{run.histogram.at(-1)!.upper.toFixed(0)}%</span></div></figure></details>}
                <details className="simulation-contract"><summary>Method and assumptions</summary>
                  <div><span>Model type</span><strong>{model.kind}</strong></div>
                  <div><span>Time step</span><strong>{model.timeStep}</strong></div>
                  <div><span>Target</span><strong>{model.target}</strong></div>
                  <div><span>Method</span><strong>{model.method}</strong></div>
                  <div><span>Equation</span><strong>{model.equation}</strong></div>
                  <div><span>Partition</span><strong>{model.partitionRule}</strong></div>
                </details>

                {run && (
                  <div className="simulation-output">
                    <header>
                      <div>
                        <span>Latest run</span>
                        <strong>{run.status} · revision {run.revision}</strong>
                      </div>
                      <div>
                        <span>Normalization</span>
                        <strong>{probabilityLabel(run.outcomes.reduce((sum, outcome) => sum + outcome.probabilityBps, 0))}</strong>
                      </div>
                      <div>
                        <span>Calibration</span>
                        <strong>{run.calibration.status} · 0 forecasts</strong>
                      </div>
                    </header>
                    <ol>
                      {run.outcomes.map((outcome) => (
                        <li key={outcome.id}>
                          <span>{outcome.title}</span>
                          <i aria-hidden="true"><b style={{ width: `${outcome.probabilityBps / 100}%` }} /></i>
                          <strong>{probabilityLabel(outcome.probabilityBps)}</strong>
                          <small>{outcome.sampleCount.toLocaleString()} paths</small>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {run?.metrics.length ? (
                  <div className="simulation-metrics">
                    {featuredMetrics.map((metric) => (
                      <div key={metric.label}>
                        <span>{metric.label}</span>
                        <strong>{metricLabel(metric.value, metric.unit, metric.label)}</strong>
                      </div>
                    ))}
                  </div>
                ) : null}

                {otherMetrics.length > 0 && <details className="simulation-more-metrics"><summary>More metrics</summary><dl>{otherMetrics.map(metric => <div key={metric.label}><dt>{metric.label}</dt><dd>{metricLabel(metric.value,metric.unit,metric.label)}</dd></div>)}</dl></details>}
                <details className="simulation-assumptions"><summary>Parameters, inputs and coverage</summary><div className="simulation-grid">
                  <section>
                    <span>Parameters · v{model.version}</span>
                    <dl>
                      {model.parameters.map((parameter) => (
                        <div key={parameter.symbol}>
                          <dt>{parameter.symbol} · {parameter.label}</dt>
                          <dd>{parameter.value}</dd>
                          <small>{parameter.provenance}</small>
                        </div>
                      ))}
                    </dl>
                  </section>
                  <section>
                    <span>Resolved inputs</span>
                    <dl>
                      {(run?.inputs ?? []).map((input) => (
                        <div key={`${input.label}-${input.sourceId ?? "derived"}`}>
                          <dt>{input.label}</dt>
                          <dd>{input.value}</dd>
                          {input.sourceId && <small>{connectorById.get(input.sourceId)?.name ?? input.sourceId}</small>}
                        </div>
                      ))}
                    </dl>
                  </section>
                  <section>
                    <span>Systems and sources</span>
                    <p>{linkedSystems.join(" · ")}</p>
                    <ul>
                      {model.requiredSourceIds.map((id) => (
                        <li key={id}>
                          <strong>{connectorById.get(id)?.name ?? id}</strong>
                          <small>{connectorById.get(id)?.status.replaceAll("_", " ") ?? "not loaded"}</small>
                        </li>
                      ))}
                    </ul>
                  </section>
                  <section>
                    <span>Known limitations</span>
                    <ul className="simulation-limitations">
                      {model.limitations.map((limitation) => <li key={limitation}>{limitation}</li>)}
                      {model.sourceGaps.map((gap) => <li key={gap}>{gap}</li>)}
                    </ul>
                  </section>
                </div></details>
              </div>
            </details>
          );
        })}
      </div>

      <details className="simulation-overview-assumptions"><summary>Assumptions and coverage</summary><p className="boundary">
        Simulation outputs are research distributions, not observed facts or proof of predictive edge. All models are versioned, seeded, uncalibrated, and disconnected from order execution.
      </p></details>
    </section>
  );
}
